import { useEffect, useState } from 'react';
import { t } from '@/i18n';
import { getCsrfToken } from '@/lib/csrf';
import type { PublicAttendee, VisibilityMode } from '../types';

interface AttendeeListProps {
  eventSlug: string;
  visibilityMode: VisibilityMode;
}

export default function AttendeeList({ eventSlug, visibilityMode }: AttendeeListProps) {
  const [attendees, setAttendees] = useState<PublicAttendee[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (visibilityMode !== 'names_and_count') {
      setLoading(false);
      return;
    }

    fetch(`/api/events/${eventSlug}/attendees/`, {
      headers: { 'X-CSRFToken': getCsrfToken() },
    })
      .then((res) => res.json())
      .then((data: PublicAttendee[]) => setAttendees(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [eventSlug, visibilityMode]);

  if (visibilityMode !== 'names_and_count' || loading) return null;
  if (attendees.length === 0) return null;

  return (
    <div className="rounded-2xl border border-gray-200 border-t-2 border-t-teal-500 bg-white p-6 shadow-sm dark:border-gray-800 dark:border-t-teal-500 dark:bg-gray-950">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
        {t('Attending')} ({attendees.reduce((sum, a) => sum + a.party_size, 0)})
      </h3>
      <ul className="mt-4 space-y-3">
        {attendees.map((a) => (
          <li key={a.id} className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              {a.full_name.charAt(0).toUpperCase()}
            </div>
            <span className="text-sm text-gray-700 dark:text-gray-300">{a.full_name}</span>
            {a.party_size > 1 && (
              <span className="text-xs text-teal-600 dark:text-teal-400">+{a.party_size - 1}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
